import { ComponentProps, FC, useCallback, useMemo } from "react";
import { useTranslation } from "@vixi/localization";
import { useNavigate } from "@vixi/router";
import { Button, cn } from "@vixi/ui";

// Assets
import PaginationArrowhead from "@assets/icons/pagination-arrowhead.svg?react";

const PaginationNavButton = ({ ...props }: ComponentProps<"button">) => {
  return (
    <Button
      {...props}
      size="icon"
      color="primary"
      variant="text"
      role="button"
      slot="paginationNavButton"
      className="size-8"
    />
  );
};

interface PageNavItemProps extends ComponentProps<"button"> {
  isSelected?: boolean;
  hide?: boolean;
}

const PageNavItem = ({ isSelected, hide, ...props }: PageNavItemProps) => {
  return (
    <span
      {...props}
      className={cn(
        "padding-2.5 fs-14 lh-1 no-select text-muted-foreground hover:not-[&:disabled]:bg-primary-light/40 box-border flex min-h-8.75 min-w-8 items-center justify-center rounded font-light",
        isSelected &&
          "fs-18 bg-primary-light hover:not-[&:disabled]:bg-primary-light font-bold text-black",
        hide && "hidden",
        props.className,
      )}
    >
      {props.children}
    </span>
  );
};

export interface PaginationNavProps {
  visiblePages?: number;
  totalPages: number;

  /**
   * The currently active page number, starting from 1.
   */
  activePage: number;
  onGoToPage?: (step: number) => void;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
}

export const PaginationNav: FC<PaginationNavProps> = ({
  visiblePages = 5,
  totalPages,
  activePage,
  onGoToPage,
  ...props
}) => {
  const { tNum, isRtl } = useTranslation();
  const navigate = useNavigate();

  const firstVisibleItem = activePage - Math.floor(visiblePages / 2);
  const isInRange = useCallback(
    (index: number) => {
      return index + firstVisibleItem > 0 && index + firstVisibleItem <= totalPages;
    },
    [firstVisibleItem, totalPages],
  );

  const goTo = useMemo(() => {
    if (onGoToPage) {
      return onGoToPage;
    } else {
      return (n: number) => {
        navigate({
          to: ".",
          replace: true,
          search: {
            pageNumber: n,
          },
        });
      };
    }
  }, [onGoToPage]);

  const hasPrev = props.hasPreviousPage ?? activePage > 1;
  const hasNext = props.hasNextPage ?? activePage < totalPages;

  return (
    <div className="box-border flex items-center justify-between px-2.5">
      <PaginationNavButton disabled={!hasPrev} onClick={() => goTo(activePage - 1)}>
        <PaginationArrowhead className="size-full rtl:-scale-x-100" />
      </PaginationNavButton>

      <div className="flex items-center gap-1">
        {Array.from({ length: visiblePages }, (_, i) =>
          i + firstVisibleItem === activePage ? (
            <PageNavItem key={i} isSelected>
              <span style={{ paddingInline: 10 }}>{tNum(i + firstVisibleItem)}</span>
            </PageNavItem>
          ) : isInRange(i) ? (
            <Button
              key={i}
              onClick={() => goTo(i + firstVisibleItem)}
              size="content"
              variant={"text"}
            >
              <PageNavItem>{tNum(i + firstVisibleItem)}</PageNavItem>
            </Button>
          ) : (
            <div key={i} className="h-8.75 w-7" />
          ),
        )}
      </div>

      <PaginationNavButton disabled={!hasNext} onClick={() => goTo(activePage + 1)}>
        <PaginationArrowhead className="size-full -scale-x-100 rtl:scale-x-100" />
      </PaginationNavButton>
    </div>
  );
};
